import { useTranslation } from 'react-i18next';
import { ActionDto } from '@/Domain/api/Action/dto/Action.dto';
import { ShopifyMcpDto } from '@/Domain/api/Action/dto/ShopifyMcp.dto';
import { UrlInput } from '@/Domain/components/KnowledgeBase/UploadUrlModal/components/UrlInput';
import { Button, IconButton } from '@/Domain/components/Shared/Button/Button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/Domain/components/Shared/Form/Form';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/Domain/components/Shared/Sheet/sheet';
import CloseIcon from '@/Domain/components/icons/Close.icon';
import { ActionTypeEnum } from '@/Domain/enum/ActionType.enum';
import { WebsiteModalTypeEnum } from '@/Domain/enum/WebsiteModalType.enum';
import { useShopifyMcpActionForm } from '@/Domain/hooks/Action/ShopifyMcp/useShopifyMcpActionForm.hook';

interface ShopifySheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  actions: ActionDto[];
}

const findShopifyAction = (actions: ActionDto[]): ShopifyMcpDto | undefined =>
  actions.find(
    (action): action is ShopifyMcpDto => action.type === ActionTypeEnum.ShopifyRemoteMcp,
  );

export const ShopifySheet = ({ open, onOpenChange, actions }: ShopifySheetProps) => {
  const { t } = useTranslation('shopify_mcp');
  const shopifyMcpAction = findShopifyAction(actions);
  const { form, handleSubmit, onInvalid } = useShopifyMcpActionForm(shopifyMcpAction);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="max-w-3xl w-full h-full" data-testid="shopify-sheet">
        <SheetHeader className="px-0.5">
          <div className="flex flex-col gap-1">
            <SheetTitle data-testid="shopify-sheet-title">{t('title')}</SheetTitle>
            <SheetDescription data-testid="shopify-sheet-description">
              {t('description')}
            </SheetDescription>
          </div>
          <IconButton
            onClick={() => onOpenChange(false)}
            variant="secondary"
            data-testid="shopify-sheet-close"
          >
            <CloseIcon />
          </IconButton>
        </SheetHeader>

        <div className="flex flex-col gap-6 overflow-y-auto px-0.5 flex-1">
          <Form {...form}>
            <form
              onSubmit={form.handleSubmit(handleSubmit, onInvalid)}
              autoComplete="off"
              className="flex flex-col gap-6"
            >
              <FormField
                name="shopify_url"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('form.shopify_url_label')}</FormLabel>
                    <FormControl>
                      <div data-testid="shopify-mcp-action-form-url">
                        <UrlInput type={WebsiteModalTypeEnum.SINGLE} {...field} />
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="flex justify-end gap-2 pt-2">
                <Button
                  variant="secondary"
                  type="button"
                  onClick={() => onOpenChange(false)}
                  data-testid="shopify-sheet-cancel"
                >
                  {t('button.cancel')}
                </Button>
                <Button variant="dark" type="submit" data-testid="shopify-sheet-submit">
                  {t('button.save')}
                </Button>
              </div>
            </form>
          </Form>
        </div>
      </SheetContent>
    </Sheet>
  );
};
